/**
 * Commission: what the advisor is actually paid, and how it splits.
 *
 * Pure functions only — no DOM, no Firebase. Every number goes through
 * `lib/money.js`; this file adds no second parser. All arithmetic is in
 * integer cents (see the CENTS note in `money.js`) so that an agent share and
 * an agency share always add back up to the commission, exactly.
 *
 * ── What the legacy actually computes ─────────────────────────────────────
 * Verified against `MBM Luxury Prototype/hosting/app.html` (15,903 lines) on
 * 15 Aug 2026. The tracker's ranges were stale; these line numbers are read,
 * not quoted.
 *
 *   5173        the Commission Tracker page: five rows of
 *               `comm{i}_company` / `comm{i}_total` / `comm{i}_pct` /
 *               `comm{i}_amt` / `comm{i}_status`, plus `commGrandTotal`
 *   6185–6193   `calcTripCommission(trip)` — sums `comm{i}_amt` over 20 slots
 *   6963–6971   `calcCommission(i)` — amt = gross * rate / 100
 *   6972–6983   `calcCommGrandTotal()` — sums the `_amt` column
 *   6985–6999   `updateCommissionSplitDisplay(total)` — agent/agency shares
 *   7000–7013   `addCommissionRow()`
 *   7074–7093   `renderFinancialDashboard()`'s commission block and split
 *   8330–8338   `getCommissionSplit()` / `setCommissionSplit()` —
 *               `mbmstudio_commSplit`, default 70 / 30
 *   8340–8365   the settings modal's split inputs and validation
 *
 * ── Five bugs found in it, each with a named regression test ──────────────
 *
 * 1. THE COMMISSION TRACKER PAGE CANNOT COMPUTE A COMMISSION.
 *    The page writes `comm{i}_total` and `comm{i}_pct` (app.html:5173).
 *    `calcCommission(i)` reads `comm{i}_gross` and `comm{i}_rate`
 *    (app.html:6966–6967). Neither name exists on that page, so both reads
 *    are `undefined`, and the function then WRITES `(0 * 0 / 100)` into the
 *    readonly amount cell on every keystroke. `commGrandTotal` follows it to
 *    "0.00", `calcTripCommission` sums zeroes, and the dashboard's
 *    "Commission Earned" card and the whole Revenue dashboard read $0 no
 *    matter how much commission the advisor has entered.
 *    `addCommissionRow()` (app.html:7000) is the only writer of the `_gross`
 *    / `_rate` names — and it queries `.commission-table tbody` while the
 *    table carries `class="traveler-table" id="commissionTable"`, so it finds
 *    nothing and adds no row. The second schema is unreachable.
 *    Both names are read here, `_total`/`_pct` first.
 *
 * 2. THE TWO SCREENS DISAGREE ABOUT THE SAME TRIP.
 *    `renderFinancialDashboard` (app.html:7084) recovers from bug 1:
 *    `if (commAmt === 0 && pct > 0 && bookingTotal > 0) commAmt = bookingTotal
 *    * pct / 100`. So the trip's own Financial Dashboard shows the right
 *    commission while the main dashboard card and Revenue dashboard show $0
 *    for the same trip at the same moment. The recovery is the correct
 *    behaviour and is what `commissionEntry` does.
 *
 * 3. THE SPLIT IS HARDCODED ON THE ONE SCREEN THAT WORKS.
 *    app.html:7090–7091 — `var agentSplitPct = 70; var agencySplitPct = 30;`
 *    `getCommissionSplit()` is never called there. An advisor who sets 60/40
 *    in Settings still sees "Your Share (70%)" on every trip dashboard, and
 *    is told they earned a number they are not owed.
 *
 * 4. `parseFloat` ON A FORMATTED TOTAL LOSES THREE ORDERS OF MAGNITUDE.
 *    `calcCommGrandTotal` writes `commGrandTotal` with
 *    `toLocaleString('en-US', {minimumFractionDigits: 2})` (app.html:6981),
 *    i.e. "12,500.00". The Pending Commission cards (app.html:5997) and the
 *    Completed/Paid cards (app.html:6049) then do
 *    `parseFloat(t.data.commGrandTotal) || 0`, and `parseFloat("12,500.00")`
 *    is **12**. An advisor owed $8,750 is shown $8.40.
 *
 * 5. THE SHARES DO NOT ADD UP, TWO DIFFERENT WAYS.
 *    `updateCommissionSplitDisplay` (app.html:6987–6988) computes both shares
 *    from their own percentage, so a stored split that does not total 100
 *    silently loses or invents money. `renderFinancialDashboard`
 *    (app.html:7092–7093) instead rounds the AGENT share to whole dollars and
 *    gives the remainder — cents and all — to the agency. Two functions, two
 *    answers for one split of one number. Here the agent share is rounded to
 *    the nearest CENT and the agency is always the remainder, so the two
 *    always sum to the commission exactly.
 */

import { amount, clampCents, fromCents, summaryNumber, toCents } from './money.js'

/** How many commission rows a trip can hold. Legacy loops 20 (app.html:7077). */
export const COMM_SLOTS = 20

/* The split half of this file moved to `lib/split.js` on 19 Aug 2026, when a
   trip gained a split of its own and this file went past the 400-line limit.
   Re-exported, so every existing importer is unchanged and there is still one
   name to reach for. Bugs 3 and 5 above are documented over there, next to
   the code that answers them. */
export {
  DEFAULT_SPLIT,
  SPLIT_ERROR,
  splitPercent,
  complementOf,
  splitBalances,
  commissionSplit,
  tripSplit,
  splitCommission,
  editSplit,
  splitToStore,
} from './split.js'

import { commissionSplit, splitCommission, tripSplit } from './split.js'

/**
 * One row of the Commission Tracker, under both of legacy's field schemas.
 *
 * `amount` is the stated `comm{i}_amt` when it is non-zero, and otherwise
 * `gross * rate / 100` — legacy's own recovery (app.html:7084), which is the
 * only reason any commission is visible anywhere today (bug 1).
 *
 * @param {Record<string, any>|null|undefined} data - `trip.data`
 * @param {number} i
 * @returns {{slot: number, company: string, gross: number, rate: number, amount: number, status: string, received: boolean, computed: boolean}}
 */
export function commissionEntry(data, i) {
  const d = data || {}
  const company = String(d[`comm${i}_company`] ?? d[`comm${i}_vendor`] ?? '').trim()
  const grossRaw = d[`comm${i}_total`] ?? d[`comm${i}_gross`]
  const rateRaw = d[`comm${i}_pct`] ?? d[`comm${i}_rate`]
  const gross = amount(grossRaw)
  const rate = amount(rateRaw)
  const stated = amount(d[`comm${i}_amt`])
  const status = String(d[`comm${i}_status`] ?? '').trim()

  let value = stated
  let computed = false
  if (stated === 0 && rate !== 0 && gross !== 0) {
    value = fromCents(clampCents((toCents(gross) * rate) / 100))
    computed = true
  }

  return {
    slot: i,
    company,
    gross,
    rate,
    amount: value,
    status,
    received: status.toLowerCase() === 'received',
    computed,
  }
}

/**
 * Every commission row a trip carries.
 *
 * Legacy drops a row unless it has a company OR a booking total
 * (app.html:7083) — which silently discards a row where the advisor typed
 * only the commission amount, while `calcTripCommission` (app.html:6185)
 * counts it. A row is kept here when it carries ANY of company, gross or
 * amount, so the list and the total can never disagree.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {number} [slots]
 * @returns {ReturnType<typeof commissionEntry>[]}
 */
export function commissionEntries(trip, slots = COMM_SLOTS) {
  const data = (trip && trip.data) || {}
  const count = Number.isFinite(slots) ? Math.max(0, Math.floor(Number(slots))) : 0
  const rows = []
  for (let i = 0; i < count; i++) {
    const entry = commissionEntry(data, i)
    if (!entry.company && entry.gross === 0 && entry.amount === 0) continue
    rows.push(entry)
  }
  return rows
}

/**
 * Total commission on a trip, before the split. Legacy `calcTripCommission`
 * (app.html:6185) plus the recovery at app.html:7084.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function tripCommission(trip) {
  const pre = summaryNumber(trip, 'commission')
  if (pre !== null) return pre
  let cents = 0
  for (const entry of commissionEntries(trip)) cents = clampCents(cents + toCents(entry.amount))
  return fromCents(cents)
}

/**
 * Commission on the rows the vendor has actually paid out (`status: Received`).
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function receivedCommission(trip) {
  const pre = summaryNumber(trip, 'commissionReceived')
  if (pre !== null) return pre
  let cents = 0
  for (const entry of commissionEntries(trip)) {
    if (entry.received) cents = clampCents(cents + toCents(entry.amount))
  }
  return fromCents(cents)
}

/**
 * The trip's commission, split. This is the number an advisor is paid.
 *
 * `split` is the account DEFAULT. A trip carrying its own overrides it — see
 * `tripSplit`.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {unknown} [split] - the account default
 * @returns {ReturnType<typeof splitCommission>}
 */
export function tripCommissionSplit(trip, split) {
  return splitCommission(tripCommission(trip), tripSplit(trip, split))
}

/**
 * The `commGrandTotal` the Commission Tracker page saved, read correctly.
 *
 * It is stored comma-formatted ("12,500.00"), and legacy reads it with a bare
 * `parseFloat`, which stops at the comma and returns 12 (bug 4). Prefer
 * `tripCommission` — this exists for reading what the page believes, and for
 * spotting when the two disagree.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function storedCommissionTotal(trip) {
  return amount((trip && trip.data && trip.data.commGrandTotal) || '')
}

/**
 * The month an advisor ticked the commission off as paid, or ''.
 *
 * Flat field first: a trip keeps this under `data`, a SUMMARY carries it
 * flattened (`trip-summary.js`), and both must answer the same. Reading only
 * `data` reported every summarised trip as never paid — which moves money from
 * "received" to "outstanding" on the dashboard without any error appearing.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @returns {string}
 */
export function commissionPaidMonth(trip) {
  if (!trip) return ''
  const flat = String(trip.commPaidMonth || '').trim()
  if (flat) return flat
  return String((trip.data && trip.data.commPaidMonth) || '').trim()
}

/**
 * Commission earned but not yet ticked off as received.
 *
 * Legacy `renderPendingCommission` (app.html:5976) selects trips whose
 * checkout has passed and which carry no `commPaidMonth`. Three things wrong
 * with it, all fixed here:
 *
 *   - `if (!t.data.checkout)` throws on a trip with no `data` at all;
 *   - `return t.status !== 'completed' || true` is `|| true`, so the whole
 *     expression is a constant and the status test does nothing — including
 *     for CANCELLED trips, which are listed as owed commission;
 *   - the amount comes from `parseFloat(commGrandTotal)` — bug 4.
 *
 * The clock and the date reader are injected: no `Date.now()` inside, and a
 * caller with real `checkout` strings supplies whatever parser it already has
 * rather than this file growing a fourth date parser.
 *
 * @param {Array<Record<string, any>>|null|undefined} trips
 * @param {{nowMs?: number, split?: unknown, checkoutMs?: (trip: any) => number|null}} [options]
 * @returns {Array<{trip: any, commission: number, agent: number, agency: number}>}
 */
export function pendingCommission(trips, options = {}) {
  const list = Array.isArray(trips) ? trips.filter(Boolean) : []
  const nowMs = Number.isFinite(options.nowMs) ? Number(options.nowMs) : 0
  const readCheckout =
    options.checkoutMs ||
    ((trip) => {
      const raw = (trip && trip.data && trip.data.checkout) || ''
      const parsed = Date.parse(String(raw))
      return Number.isFinite(parsed) ? parsed : null
    })

  const out = []
  for (const trip of list) {
    const status = String(trip.status || '').toLowerCase()
    if (status === 'cancelled') continue
    if (commissionPaidMonth(trip)) continue
    const checkout = readCheckout(trip)
    if (checkout == null || !Number.isFinite(checkout) || checkout > nowMs) continue
    /* Each trip on ITS split. `options.split` is the account default and is
       only reached for a trip that has not been given one. */
    const split = splitCommission(tripCommission(trip), tripSplit(trip, options.split))
    if (split.total === 0) continue
    out.push({ trip, commission: split.total, agent: split.agent, agency: split.agency })
  }
  return out
}

/**
 * Commission across many trips.
 *
 * ── Split per trip, then summed ───────────────────────────────────────────
 *
 * This used to split the TOTAL once at the end, which was the cheaper
 * rounding — N trips split individually drift by up to N/2 cents from the
 * split of the sum. That stopped being available on 19 Aug 2026, when a trip
 * gained a split of its own: with a 70/30 trip and an 80/20 trip in the list
 * there is no single percentage to apply to the sum, and using one is not a
 * rounding difference but a wrong answer.
 *
 * So each trip is split on its own terms and the shares are added. `agent +
 * agency === gross` still holds exactly, because both sides are summed in
 * cents from splits that each balance.
 *
 * `agentPct` / `agencyPct` are the EFFECTIVE percentages — what the advisor
 * actually kept across the whole list. On one split they are that split; on a
 * mix they are the blend, which is the honest number to show. With no
 * commission to take a percentage of they are the account default, so a
 * brand-new dashboard still reads "Your share (70%)" rather than "(0%)".
 *
 * Cancelled trips are worth nothing anywhere (money.js).
 *
 * @param {Array<Record<string, any>>|null|undefined} trips
 * @param {unknown} [split] - the account default, for trips without their own
 * @returns {{trips: number, gross: number, agent: number, agency: number, received: number, agentPct: number, agencyPct: number, mixed: boolean}}
 */
export function commissionTotals(trips, split) {
  const list = Array.isArray(trips) ? trips.filter(Boolean) : []
  let grossCents = 0
  let agentCents = 0
  let receivedCents = 0
  let counted = 0
  /* Whether the list is all on one split, so a caller can say "70%" plainly
     rather than presenting a blended average as if it were a rate anyone set. */
  const seen = new Set()

  for (const trip of list) {
    if (String(trip.status || '').toLowerCase() === 'cancelled') continue
    counted++
    const shares = splitCommission(tripCommission(trip), tripSplit(trip, split))
    grossCents = clampCents(grossCents + toCents(shares.total))
    agentCents = clampCents(agentCents + toCents(shares.agent))
    receivedCents = clampCents(receivedCents + toCents(receivedCommission(trip)))
    seen.add(`${shares.agentPct}/${shares.agencyPct}`)
  }

  const agencyCents = clampCents(grossCents - agentCents)
  const agentPct = grossCents === 0 ? commissionSplit(split).agent : (agentCents / grossCents) * 100
  return {
    trips: counted,
    gross: fromCents(grossCents),
    agent: fromCents(agentCents),
    agency: fromCents(agencyCents),
    received: fromCents(receivedCents),
    agentPct,
    agencyPct: 100 - agentPct,
    mixed: seen.size > 1,
  }
}

